// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.
import { Mrq10KSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-10-k-system-model';
import { Mrq4KSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-4-k-system-model';
import { MrqDryCoolSystemModel } from '../../../../api/remote-api/.portal.generated/models/mrq-dry-cool-system-model';

import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  filter,
  finalize,
  map,
  of,
  takeUntil,
  tap,
} from 'rxjs';
import { ExtendedComponentModel } from 'src/app/_interfaces/component-model-extension';
import {
  ComponentModel,
  SystemDetailsModel,
} from 'src/app/api/remote-api/.portal.generated/models';
import { SystemOverviewService } from 'src/app/modules/portal/services/systemOverview.service';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { NoDataIconComponent } from 'src/app/modules/shared/components/no-data-icon/no-data-icon.component';
import { TippyTooltipComponent } from 'src/app/modules/shared/components/tippy-tooltip/tippy-tooltip.component';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { MapApiService } from 'src/app/modules/shared/services/map-api.service';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { Observe } from 'src/app/modules/shared/utils/as-observable';
import { MrqReportData } from '../../interfaces/mrq.interfaces';
import { MrqReportService } from '../../services/mrq-report/mrq-report.service';
import {
  PatternMatchDataService,
  PatternMatchModelWithId,
} from '../../services/pattern-match-data/pattern-match-data.service';
import { MrqTableComponent } from '../mrq-table/mrq-table.component';
import {
  MouseEventData,
  PatternMatchChartComponent,
} from '../pm-chart/pm-chart.component';
import { aggregateBinsFromAnalysis } from '../pm-chart/utils';
import { PatternMatchTableComponent } from '../pm-table/pm-table.component';

@Component({
  selector: 'app-pattern-match',
  templateUrl: './pattern-match.component.html',
  styleUrls: ['./pattern-match.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    TranslateModule,
    APICallGateComponent,
    PatternMatchChartComponent,
    LocalizedDatePipe,
    PatternMatchTableComponent,
    NoDataIconComponent,
    MrqTableComponent,
    CommonModule,
    TippyTooltipComponent,
  ],
})
export class PatternMatchComponent implements OnDestroy, OnInit {
  protected mapApiservice = inject(MapApiService);
  protected patternMatchDataService = inject(PatternMatchDataService);
  private systemOverviewService = inject(SystemOverviewService);
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  protected mrqReportService = inject(MrqReportService);

  @Input({ required: true }) public systemDetails!: SystemDetailsModel;
  @Input({ required: true }) public filterName!: string;
  @Input({ required: false }) public rangeInDays = 7;

  // hardcoded 1 week range for now!
  protected fixedDateRange$ = of({
    min: moment().subtract(1, 'week').add(1, 'day').startOf('day').toDate(),
    max: moment().endOf('day').toDate(),
  });

  @Observe('systemDetails')
  private systemDetails$!: Observable<typeof this.systemDetails>;
  @Observe('filterName')
  readonly filterName$!: Observable<typeof this.filterName>;
  @Observe('rangeInDays')
  private rangeInDays$!: Observable<typeof this.rangeInDays>;

  @HostBinding('style.boxShadow') get boxShadow(): string {
    return this.isHostBoxShadowDisabled ? 'none' : 'var(--shadow-raised)';
  }

  mrqData = this.mrqReportService.getMrqDashboard.lastSuccessfulData$;
  navServiceResponse: ComponentModel | null = null;
  isHostBoxShadowDisabled = false;
  selectedBar$ = this.patternMatchDataService.selectedBar$;
  iconColor = '#C8C8C8';

  // selected from chart
  hoveredItem?: DeepReadonly<MouseEventData>;

  // selected from table
  selectedRow$ = new BehaviorSubject<PatternMatchModelWithId | undefined>(
    undefined,
  );

  // Tab constants
  readonly patternMatchTab = 'mrqLabel.PatternMatches';
  readonly mrqReports = 'mrqLabel.mrqReport';
  activeTab = this.patternMatchTab;

  // MRQ Report properties
  mrqReportData: MrqReportData[] = [];
  mrqMagnetType: string = '4K'; // Default magnet type
  mrqLoading: boolean = false;

  // Computed property to get the appropriate legend based on magnet type
  get currentMagnetLegend(): string {
    return this.patternMatchDataService.getMagnetTypeLegend(this.mrqMagnetType);
  }

  destroy$ = new Subject<void>();
  private isGoingToPreviousPage = false;

  ngOnInit(): void {
    this.boxShadowStatus$.subscribe();
    this.determineMagnetType$.pipe(takeUntil(this.destroy$)).subscribe();
    this.switchToMrqReportTab$.subscribe();
  }

  ngOnDestroy(): void {
    // Only clear selected bar if user goes to the previous page
    if (!this.isGoingToPreviousPage) {
      this.selectedBar$.next(undefined);
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.selectedRow$.complete();
  }

  pmAnalysis = this.patternMatchDataService.pmAnalysis;

  analysis$ = this.pmAnalysis.lastSuccessfulData$;

  patternMatches$ = this.analysis$.pipe(map((a) => a.patternMatches));

  isItMagnet$ = this.systemOverviewService.magnetType.lastSuccessfulData$;

  lastUpdate$ = this.patternMatches$.pipe(
    map((matches) => {
      let lastUpdate = moment(0);
      matches.forEach(({ matchFindings }) =>
        matchFindings.forEach(
          ({ date }) =>
            moment(date).isAfter(lastUpdate) && (lastUpdate = moment(date)),
        ),
      );
      return lastUpdate;
    }),
  );

  binnedResults$ = combineLatest({
    analysis: this.analysis$,
    timelineAscending: this.settingsService.timelineAscending$,
    selectedRow: this.selectedRow$,
    selectedBar: this.selectedBar$,
    fixedDateRange: this.fixedDateRange$,
  }).pipe(
    map(
      ({
        analysis,
        selectedRow,
        selectedBar,
        timelineAscending,
        fixedDateRange,
      }) => {
        // focus on a bar or a row
        return aggregateBinsFromAnalysis(analysis.patternMatches, {
          focusId: selectedRow?.id,
          focusDate: selectedBar ? moment(selectedBar?.bin.date) : undefined,
          ascending: timelineAscending,
          fixedDateRange,
          addDatesInDateRange: true,
        });
      },
    ),
  );

  hasMatches$ = this.binnedResults$.pipe(
    map(({ patternMatches }) => patternMatches.length > 0),
  );

  requestParams$ = combineLatest({
    systemDetails: this.systemDetails$,
    rangeInDays: this.rangeInDays$,
    modalities: this.systemOverviewService.modalities.lastSuccessfulData$,
    filterName: this.filterName$,
  }).pipe(
    filter(
      ({ systemDetails, rangeInDays, modalities, filterName }) =>
        !!systemDetails && !!rangeInDays && !!modalities && !!filterName,
    ),

    takeUntil(this.destroy$),
  );

  requestParamsSubscription = this.requestParams$.subscribe(
    ({ systemDetails, rangeInDays, modalities, filterName }) => {
      this.pmAnalysis.request({
        systemMaterialNumber: systemDetails.systemMaterialNumber,
        systemSerialNumber: systemDetails.systemSerialNumber,
        rangeInDays: rangeInDays,
        modality:
          modalities.find((m) => m.id === this.systemDetails.modalityId)
            ?.displayName ?? '',
        componentName: filterName,
      });
    },
  );

  boxShadowStatus$ = combineLatest([this.hasMatches$, this.filterName$]).pipe(
    takeUntil(this.destroy$),
    tap(([hasMatches, filterName]) => {
      this.isHostBoxShadowDisabled = !hasMatches && filterName === 'n.a.';
    }),
  );

  activateTab(tab: string): void {
    this.setActiveTab(tab);
    this.updateTabQueryParams(tab);
    this.loadMrqDataIfNeeded(tab);
  }

  private setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  private updateTabQueryParams(tab: string): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    if (tab === this.mrqReports) {
      queryParams['tab'] = 'mrq-reports';
    } else {
      delete queryParams['tab'];
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  private loadMrqDataIfNeeded(tab: string): void {
    if (tab === this.mrqReports && this.mrqReportData.length === 0) {
      this.loadMrqReportData();
    }
  }

  determineMagnetType$ =
    this.systemOverviewService.magnetType.lastSuccessfulData$.pipe(
      map((magnetType) => {
        this.mrqMagnetType = this.resolveMagnetType(magnetType);
      }),
    );

  private resolveMagnetType(magnetType: unknown): string {
    // Default value
    let resolvedType = '4K';
    if (magnetType) {
      const magnetTypeStr = magnetType.toString().toLowerCase();
      if (magnetTypeStr === '10k') {
        resolvedType = '10K';
      } else if (magnetTypeStr === 'dry') {
        resolvedType = 'DryCool';
      }
    }
    return resolvedType;
  }

  rowClicked(item: PatternMatchModelWithId): void {
    // disable current bar
    this.selectedBar$.next(undefined);
    this.hoveredItem = undefined;

    if (item.id !== this.selectedRow$.value?.id) {
      // select
      this.selectedRow$.next(item);
    } else {
      // unselect
      this.selectedRow$.next(undefined);
    }
  }

  barClicked(bar: MouseEventData): void {
    // Disable current row
    this.selectedRow$.next(undefined);
    this.hoveredItem = undefined;

    if (
      this.selectedBar$.value?.bin.date.getTime() === bar.bin.date.getTime()
    ) {
      // Unselect the bar
      this.selectedBar$.next(undefined);
    } else {
      // Select the bar
      if (!this.router.url.includes('component')) {
        this.gotoPatternComponent(bar);
      } else {
        this.selectedBar$.next(bar);
      }
    }
  }

  gotoPatternComponent(bar: MouseEventData): void {
    const component = bar.item.raw;
    this.isGoingToPreviousPage = true;
    this.patternMatchDataService.goToComponent(
      component as unknown as ExtendedComponentModel,
      this.route,
    );
    this.selectedBar$.next(bar);
  }

  isComponentStringInUrl = (): boolean => this.router.url.includes('component');

  // Load MRQ report data
  private loadMrqReportData(): void {
    this.mrqLoading = true;
    this.mrqReportService
      .getMrqReportData(
        this.systemDetails.systemSerialNumber,
        this.systemDetails.systemMaterialNumber,
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.mrqLoading = false)),
      )
      .subscribe((data) => {
        this.mrqReportData = this.selectMrqReportDataByMagnetType(data);
      });
  }

  openDashboardInNewWindow(url: string | null | undefined): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  private selectMrqReportDataByMagnetType(data: {
    magnet4k: Mrq4KSystemModel[];
    magnet10k: Mrq10KSystemModel[];
    magnetDryCool: MrqDryCoolSystemModel[];
  }): MrqReportData[] {
    switch (this.mrqMagnetType) {
      case '4K':
        return this.mrqReportService.mapToMrqReportData(data.magnet4k, '4K');
      case '10K':
        return this.mrqReportService.mapToMrqReportData(data.magnet10k, '10K');
      case 'DryCool':
        return this.mrqReportService.mapToMrqReportData(
          data.magnetDryCool,
          'DryCool',
        );
      default:
        return [];
    }
  }

  // Handle query parameters for tab switching
  private switchToMrqReportTab$ = this.route.queryParams.pipe(
    takeUntil(this.destroy$),
    map((params) => {
      if (params['tab'] === 'mrq-reports') {
        this.activeTab = this.mrqReports;
        // Load MRQ data if not already loaded
        if (this.mrqReportData.length === 0) {
          this.loadMrqReportData();
        }
      }
    }),
  );
}
